import { useRef } from 'react';

import { Moon, Sun } from 'lucide-react';

import { Button, cn, FileUpload } from '@src/components/ui';
import { LANGUAGES } from '@src/constants';
import { useTheme } from '@src/context/hooks';
import {
  EmailField,
  Form,
  PasswordField,
  SelectField,
  Submit,
  TextField,
  useFormContext,
} from '@src/corelib/forms/generic';
import {
  useMeQuery,
  usePasswordChangeMutation,
  useProfileAvatarMutation,
  useProfileSaveMutation,
} from '@src/query';

import { Toaster, useToasts } from '../Listings/useToasts';
import { initials } from './bits';
import { Field } from './fields';
import PageHeader from './PageHeader';
import { passwordDefaults, passwordSchema, profileDefaults, profileSchema } from './profileForm';


const LANGUAGE_OPTIONS = LANGUAGES.map(([value, label]) => ({ value, label }));


/** Avatar block: gradient initials (or current image) + Change photo / Remove. */
function AvatarRow({ me, onUpload, onRemove, busy }) {
  return (
    <div className="flex items-center gap-3.5">
      {me?.avatar
        ? (
          <img
            alt=""
            src={me.avatar}
            className="size-14 shrink-0 rounded-full object-cover"
          />
        )
        : (
          <span
            className={cn(
              'bg-brand-gradient grid size-14 shrink-0 place-items-center rounded-full',
              'text-lg font-bold text-white',
            )}
          >
            {initials(me)}
          </span>
        )}

      <FileUpload
        accept="image/*"
        onSelect={(files) => {
          const file = files[0];
          if (file) {
            onUpload(file);
          }
        }}
      >
        {({ open }) => (
          <Button
            type="button"
            variant="secondary"
            className="h-9 px-3.5 text-table"
            loading={busy}
            onClick={open}
          >
            Change photo
          </Button>
        )}
      </FileUpload>

      {me?.avatar
        ? (
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            className="h-9 px-0 text-table text-ink-subtle hover:bg-transparent hover:text-ink"
            onClick={onRemove}
          >
            Remove
          </Button>
        )
        : null}
    </div>
  );
}

AvatarRow.displayName = 'AvatarRow';


/** Change-password card (#145, djoser set_password). Per-field server errors map
 * onto the matching inputs via the Form's `showServerErrors`. */
function PasswordCard({ onToast }) {
  const mutation = usePasswordChangeMutation();
  const resetRef = useRef(null);

  const submit = (values) => mutation.mutateAsync({ payload: values })
    .then(() => {
      resetRef.current?.(passwordDefaults);
      onToast('Password updated');
    });

  return (
    <Form
      schema={passwordSchema}
      defaultValues={passwordDefaults}
      className="flex flex-col gap-3.5 rounded-lg border border-hairline bg-surface-1 p-4"
      onSubmit={submit}
      onError={(error, meta) => {
        if (!meta?.isInvalid) {
          onToast('Could not update password', 'danger');
        }
      }}
    >
      <PasswordCardBody
        registerReset={(fn) => {
          resetRef.current = fn;
        }}
      />
    </Form>
  );
}

PasswordCard.displayName = 'PasswordCard';


/** Inner body that registers the form `reset` up to the card and gates submit. */
function PasswordCardBody({ registerReset }) {
  const { watch, reset } = useFormContext();
  registerReset(reset);
  const values = watch();
  const filled = values.current_password && values.new_password && values.re_new_password;

  return (
    <>
      <span className="text-card-title font-semibold text-ink">Change password</span>

      <PasswordField
        name="current_password"
        label="Current password"
        autoComplete="current-password"
      />

      <div className="flex flex-col gap-3.5 sm:flex-row">
        <PasswordField
          className="flex-1"
          name="new_password"
          label="New password"
          autoComplete="new-password"
        />

        <PasswordField
          className="flex-1"
          name="re_new_password"
          label="Confirm new password"
          autoComplete="new-password"
        />
      </div>

      <div className="flex">
        <Submit disabled={!filled} variant="secondary" className="h-[42px] px-[18px] text-sm">
          Update password
        </Submit>
      </div>
    </>
  );
}

PasswordCardBody.displayName = 'PasswordCardBody';


/** Theme + language row. Theme is a button toggle (not a form field); language is
 * a disabled single-option select. Lives inside the profile `<Form>`. */
function PreferencesRow() {
  const theme = useTheme();

  return (
    <div className="flex flex-col gap-3.5 sm:flex-row">
      <Field label="Theme" className="flex-1">
        <Button
          type="button"
          variant="secondary"
          className="h-[42px] justify-between px-3 text-sm font-medium"
          onClick={theme?.toggle}
        >
          <span>{theme?.isLight ? 'Light' : 'Dark'}</span>

          {theme?.isLight
            ? <Sun className="size-[15px] text-ink-subtle" />
            : <Moon className="size-[15px] text-ink-subtle" />}
        </Button>
      </Field>

      <div className="flex-1">
        <SelectField disabled name="language" label="Language" options={LANGUAGE_OPTIONS} />
      </div>
    </div>
  );
}

PreferencesRow.displayName = 'PreferencesRow';


/** Watches the email field so the "you will need to re-verify" hint mirrors the
 * original live comparison against the loaded address. */
function EmailReverifyHint({ currentEmail }) {
  const { watch } = useFormContext();
  const emailChanged = (watch('email') || '') !== currentEmail;

  if (!emailChanged) {
    return null;
  }
  return <span className="text-xs text-ink-tertiary">· you will need to re-verify</span>;
}

EmailReverifyHint.displayName = 'EmailReverifyHint';


/** Profile page (#145, .pen WWmHq): personal account — name, email (change
 * resets verification), avatar, password, theme + language. */
function Profile() {
  const { toasts, push } = useToasts();
  const { data: me, queryKey } = useMeQuery();

  const saveMutation = useProfileSaveMutation();
  const avatarMutation = useProfileAvatarMutation();

  if (!me) {
    return null;
  }

  const save = (values) => {
    const emailChanged = values.email !== me.email;
    // Only the round-tripping fields go to the API — `language` is UI-only.
    const payload = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
    };
    return saveMutation.mutateAsync({ payload, queryKey })
      .then(() => push(emailChanged ? 'Saved — confirm your new email' : 'Profile saved'));
  };

  const uploadAvatar = (file) => {
    const body = new FormData();
    body.append('avatar', file);
    avatarMutation.mutate(
      { payload: body, contentType: 'multipart/form-data', queryKey },
      {
        onSuccess: () => push('Photo updated'),
        onError: () => push('Could not upload photo', 'danger'),
      },
    );
  };

  const removeAvatar = () => {
    avatarMutation.mutate(
      { payload: { avatar: null }, queryKey },
      {
        onSuccess: () => push('Photo removed'),
        onError: () => push('Could not remove photo', 'danger'),
      },
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader title="Profile" subtitle="Your personal account" />

      <div className="flex w-full max-w-[640px] flex-col gap-4 px-4 pb-6 lg:px-0">
        <AvatarRow
          me={me}
          busy={avatarMutation.isPending}
          onUpload={uploadAvatar}
          onRemove={removeAvatar}
        />

        <Form
          schema={profileSchema}
          className="flex flex-col gap-4"
          defaultValues={{ ...profileDefaults(me), language: LANGUAGE_OPTIONS[0]?.value || 'en' }}
          onSubmit={save}
          onError={(error, meta) => {
            if (!meta?.isInvalid) {
              push('Could not save profile', 'danger');
            }
          }}
        >
          <div className="flex flex-col gap-3.5 sm:flex-row">
            <TextField name="first_name" label="First name" className="flex-1" />
            <TextField name="last_name" label="Last name" className="flex-1" />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="flex items-center gap-2">
              <span className="text-xs font-medium text-ink-subtle">Email</span>
              <EmailReverifyHint currentEmail={me.email} />
            </span>

            <EmailField name="email" />
          </div>

          <PreferencesRow />

          <div className="flex">
            <Submit className="h-[42px] px-[22px] text-sm">Save changes</Submit>
          </div>
        </Form>

        <PasswordCard onToast={push} />
      </div>

      <Toaster toasts={toasts} />
    </div>
  );
}

Profile.displayName = 'Profile';
export default Profile;
